import * as React from 'react'
import isString from 'lodash/isString'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { cn } from '@klear/ui/lib/utils'
import { Button } from '@klear/ui/components/button'
import { Badge } from '@klear/ui/components/badge'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@klear/ui/components/command'
import { Popover, PopoverContent, PopoverTrigger } from '@klear/ui/components/popover'
import type { ComboboxOption } from '@klear/ui/components/combobox'

interface ComboboxMultiProps<T = string> {
  options: ComboboxOption<T>[]
  value: ComboboxOption<T>[]
  onChange: (value: ComboboxOption<T>[]) => void
  disabled?: boolean
  placeholder?: string
  triggerLabel?: string
  triggerIcon?: React.ReactNode
  className?: string
  error?: { message?: string }
  valueType?: 'string' | 'number'
  size?: 'default' | 'sm' | 'xs' | 'lg'
}

function ComboboxMultiComponent<T = string>(
  {
    options,
    value,
    onChange,
    disabled = false,
    placeholder = 'Search...',
    triggerLabel = 'Select...',
    triggerIcon = null,
    className,
    error,
    size = 'default'
  }: ComboboxMultiProps<T>,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const [open, setOpen] = React.useState(false)
  const selectedMap = React.useMemo(() => {
    const map = new Map<T, ComboboxOption<T>>()
    value.forEach((v) => map.set(v.value, v))
    return map
  }, [value])

  const handleSelect = (option: ComboboxOption<T>) => {
    if (selectedMap.has(option.value)) {
      onChange(value.filter((v) => v.value !== option.value))
    } else {
      onChange([...value, { ...option }])
      setOpen(false)
    }
  }

  const handleRemove = (val: ComboboxOption<T>) => {
    onChange(value.filter((v) => v.value !== val.value))
  }

  const optionsLocal = (options || []).map((option) => ({
    ...option,
    keywords: [...(option.keywords ?? []), ...(isString(option.label) ? [option.label] : []), `${option.value}`]
  }))

  return (
    <div className={cn('w-full', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            tabIndex={0}
            size={size}
            className={cn('justify-between w-full', error && 'border-red-500')}
            disabled={disabled}
          >
            {triggerIcon}
            <span className="truncate flex-1 text-left">
              {value.length > 0 ? value.map((v) => v.label).join(', ') : (triggerLabel ?? placeholder)}
            </span>
            <ChevronsUpDown className="opacity-50 ml-2" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[300px]">
          <Command>
            <CommandInput
              placeholder={placeholder}
              className={size === 'sm' || size === 'xs' ? 'h-8 text-sm' : 'h-9'}
            />
            <CommandList>
              <CommandEmpty>No results.</CommandEmpty>
              <CommandGroup>
                {optionsLocal.map((option) => {
                  const isSelected = selectedMap.has(option.value)
                  return (
                    <CommandItem
                      key={`${option.value}`}
                      className="cursor-pointer"
                      value={`${option.value}`}
                      keywords={option.keywords}
                      onSelect={() => handleSelect(option)}
                    >
                      <span>{option.label}</span>
                      {isSelected && <Check className="ml-auto opacity-100" />}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {value.map((val) => (
            <Badge key={`${val.value}`} variant="secondary" className="flex items-center gap-1 pr-1">
              <span>{val.label}</span>
              <Button
                type="button"
                size="xs"
                variant="ghost"
                className="ml-1 p-0 h-4 w-4"
                onClick={() => handleRemove(val)}
                tabIndex={-1}
                aria-label="Remove"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
      {error?.message && <p className="text-sm text-red-500 mt-1">{error.message}</p>}
    </div>
  )
}

export const ComboboxMulti = React.forwardRef(ComboboxMultiComponent) as unknown as {
  <T = string>(props: ComboboxMultiProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement> }): JSX.Element
  displayName?: string
}

ComboboxMulti.displayName = 'ComboboxMulti'
